import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const DiscussionForum = ({ onViewFullForum }) => {
  const [newPost, setNewPost] = useState('');

  const forumPosts = [
    {
      id: 1,
      author: "Sarah Chen",
      role: "Developer",
      avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150",
      title: "Best practices for IoT sensor data collection?",
      content: "I\'m working on a smart parking project and wondering about the most efficient ways to collect and process sensor data. Any recommendations for protocols or frameworks?",
      timestamp: new Date(Date.now() - 3600000),
      replies: 8,
      likes: 12,
      tags: ["IoT", "Data Collection", "Sensors"]
    },
    {
      id: 2,
      author: "Marcus Johnson",
      role: "Project Manager",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150",
      title: "Team coordination tools for remote collaboration",
      content: "Our team is distributed across different cities. What tools have you found most effective for managing smart city projects remotely?",
      timestamp: new Date(Date.now() - 7200000),
      replies: 15,
      likes: 23,
      tags: ["Project Management", "Remote Work", "Tools"]
    },
    {
      id: 3,
      author: "Elena Rodriguez",
      role: "Business Analyst",
      avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150",
      title: "ROI calculation for smart city initiatives",
      content: "How do you approach calculating return on investment for smart city projects? Looking for frameworks or case studies.",
      timestamp: new Date(Date.now() - 10800000),
      replies: 6,
      likes: 18,
      tags: ["ROI", "Business Analysis", "Case Studies"]
    }
  ];

  const handlePostSubmit = (e) => {
    e?.preventDefault();
    if (newPost?.trim()) {
      console.log('New post:', newPost);
      setNewPost('');
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    
    if (hours < 1) return 'Just now';
    if (hours === 1) return '1 hour ago';
    if (hours < 24) return `${hours} hours ago`;
    
    const days = Math.floor(hours / 24);
    if (days === 1) return '1 day ago';
    return `${days} days ago`;
  };

  const getRoleColor = (role) => {
    const colors = {
      'Developer': 'bg-blue-100 text-blue-800',
      'Project Manager': 'bg-green-100 text-green-800',
      'Business Analyst': 'bg-purple-100 text-purple-800',
      'Product Designer': 'bg-orange-100 text-orange-800'
    };
    return colors?.[role] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-foreground">Discussion Forum</h2>
        <Button variant="ghost" size="sm" onClick={onViewFullForum} iconName="MessageSquare" iconPosition="right">
          View All
        </Button>
      </div>
      {/* Quick Post */}
      <form onSubmit={handlePostSubmit} className="mb-6">
        <div className="flex space-x-3">
          <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-xs font-medium text-primary-foreground">JD</span>
          </div>
          <div className="flex-1">
            <Input
              type="text"
              placeholder="Ask a question or share your thoughts..."
              value={newPost}
              onChange={(e) => setNewPost(e?.target?.value)}
              className="mb-2"
            />
            <div className="flex justify-end">
              <Button 
                type="submit" 
                size="sm" 
                disabled={!newPost?.trim()}
                iconName="Send" 
                iconPosition="right"
              >
                Post
              </Button>
            </div>
          </div>
        </div>
      </form>
      {/* Recent Posts */}
      <div className="space-y-4">
        {forumPosts?.map((post) => (
          <div key={post?.id} className="border border-border rounded-lg p-4 hover:border-primary/20 transition-all duration-200">
            <div className="flex items-start space-x-3">
              <img
                src={post?.avatar}
                alt={post?.author}
                className="w-10 h-10 rounded-full object-cover"
                onError={(e) => {
                  e.target.src = '/assets/images/no_image.png';
                }}
              />
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2 mb-1">
                  <h4 className="font-medium text-foreground text-sm">{post?.author}</h4>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleColor(post?.role)}`}>
                    {post?.role}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {formatTimeAgo(post?.timestamp)}
                  </span>
                </div>
                
                <h3 className="font-medium text-foreground mb-2 line-clamp-1">
                  {post?.title}
                </h3>
                
                <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                  {post?.content}
                </p>
                
                <div className="flex flex-wrap gap-1 mb-3">
                  {post?.tags?.map((tag, index) => (
                    <span key={index} className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded">
                      {tag}
                    </span>
                  ))}
                </div>
                
                <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                  <button className="flex items-center space-x-1 hover:text-primary transition-micro">
                    <Icon name="Heart" size={14} />
                    <span>{post?.likes}</span>
                  </button>
                  <button className="flex items-center space-x-1 hover:text-primary transition-micro">
                    <Icon name="MessageCircle" size={14} />
                    <span>{post?.replies} replies</span>
                  </button>
                  <button className="flex items-center space-x-1 hover:text-primary transition-micro">
                    <Icon name="Share" size={14} />
                    <span>Share</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 pt-4 border-t border-border text-center">
        <Button variant="outline" onClick={onViewFullForum} iconName="MessageSquare" iconPosition="left">
          Join Full Discussion
        </Button>
      </div>
    </div>
  );
};

export default DiscussionForum;